/**
 * Datos generales del tenant: nombre comercial editable, zona horaria y
 * los read-only (slug, país, moneda). El nombre se edita acá y no en el
 * header del detalle para mantener la sección compacta y gateada detrás
 * del collapsible.
 */

import { Button, CollapsibleSection } from '@atreus/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { apiPatch } from '../../cloud.js';

// Zonas horarias de Colombia — lista cortita porque el país es único
// por ahora. Cuando soportemos otros países, vendrá de un helper en core.
const TIMEZONES = ['America/Bogota'];

interface Props {
  tenantId: string;
  tenant: {
    slug: string;
    name: string;
    country: string;
    currency: string;
    timezone: string;
  };
}

export const TenantGeneralSection = ({ tenantId, tenant }: Props) => {
  const qc = useQueryClient();
  const [name, setName] = useState(tenant.name);
  const [timezone, setTimezone] = useState(tenant.timezone);

  useEffect(() => {
    setName(tenant.name);
    setTimezone(tenant.timezone);
  }, [tenant.name, tenant.timezone]);

  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ ok: true }>(`/admin/tenants/${tenantId}`, {
        name: name.trim(),
        timezone,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] }),
  });

  const dirty = name.trim() !== tenant.name || timezone !== tenant.timezone;
  const canSave = dirty && name.trim().length > 0 && !save.isPending;

  return (
    <CollapsibleSection
      title="Información general"
      icon={Building2}
      persistKey={`tenant-general-${tenantId}`}
      defaultOpen
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label
            htmlFor="gen-name"
            className="mb-1 block text-sm font-semibold"
          >
            Nombre comercial
          </label>
          <input
            id="gen-name"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold">Slug</label>
          <input
            value={tenant.slug}
            readOnly
            className="w-full cursor-not-allowed rounded-md border border-border bg-surface-muted px-3 py-2 font-mono text-sm text-fg-muted"
          />
        </div>
        <div>
          <label
            htmlFor="gen-tz"
            className="mb-1 block text-sm font-semibold"
          >
            Zona horaria
          </label>
          <select
            id="gen-tz"
            autoComplete="off"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold">País</label>
          <input
            value={tenant.country}
            readOnly
            className="w-full cursor-not-allowed rounded-md border border-border bg-surface-muted px-3 py-2 text-base text-fg-muted"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold">Moneda</label>
          <input
            value={tenant.currency}
            readOnly
            className="w-full cursor-not-allowed rounded-md border border-border bg-surface-muted px-3 py-2 text-base text-fg-muted"
          />
        </div>
      </div>

      {save.error ? (
        <p className="mt-3 text-sm text-danger">
          {(save.error as Error).message}
        </p>
      ) : null}

      <div className="mt-4 flex justify-end">
        <Button disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </CollapsibleSection>
  );
};
